<template>
    <div :class="barClass" @mousedown="clickTrackHandler">
        <div ref="thumb" class="sy-scrollbar-bar-thumb" :style="thumbStyle" @mousedown="clickThumbHandler" />
    </div>
</template>

<script>
import { BAR_MAP, renderThumbStyle } from './util';

/**
 * 滑动条组件,仅供sy-scrollbar内部使用,非公共组件
 */
export default {
    name: 'Bar',
    props: {
        /**
         * 是否垂直滑动条
         */
        vertical: {
            type: Boolean,
            required: false,
            default: false
        },
        /**
         * 滑动条宽度
         */
        size: {
            type: String,
            required: true
        },
        /**
         * 滑块在滑动条的位置
         */
        move: {
            type: Number,
            required: true
        }
    },
    data() {
        return {
            // 鼠标是否按下
            cursorDown: false
        };
    },
    computed: {
        /**
         * 滑块属性对象计算值
         */
        bar() {
            return BAR_MAP[this.vertical ? 'vertical' : 'horizontal'];
        },
        /**
         * 父组件包装器引用计算值
         */
        wrap() {
            return this.$parent.wrap;
        },
        /**
         * 滑动条样式类计算值
         */
        barClass() {
            return ['sy-scrollbar-bar', `is-${this.bar.key}`];
        },
        /**
         * 滑动条样式对象计算值
         */
        thumbStyle() {
            return renderThumbStyle({
                size: this.size,
                move: this.move,
                bar: this.bar
            });
        }
    },
    /**
     * 组件渲染完成方法
     */
    mounted() {
        // 订阅事件
        document.addEventListener('mousemove', this.mouseMoveDocumentHandler);
        document.addEventListener('mouseup', this.mouseUpDocumentHandler);
        document.addEventListener('selectstart', this.mouseSelectStartDocumentHandler);
    },
    /**
     * 组件销毁方法
     */
    destroyed() {
        // 取消订阅事件
        document.addEventListener('mousemove', this.mouseMoveDocumentHandler);
        document.addEventListener('mouseup', this.mouseUpDocumentHandler);
        document.addEventListener('selectstart', this.mouseSelectStartDocumentHandler);
    },
    methods: {
        /**
         * 滑块点击事件
         * @param e 事件参数
         */
        clickThumbHandler(e) {
            // 开始拖拽
            e.stopImmediatePropagation();
            this.cursorDown = true;

            // 记录拖拽开始位置
            this[this.bar.axis] =
                e.currentTarget[this.bar.offset] -
                (e[this.bar.client] - e.currentTarget.getBoundingClientRect()[this.bar.direction]);
        },
        /**
         * 滑动条点击事件
         * @param e 事件参数
         */
        clickTrackHandler(e) {
            const offset = Math.abs(e.target.getBoundingClientRect()[this.bar.direction] - e[this.bar.client]);
            const thumbHalf = this.$refs.thumb[this.bar.offset] / 2;
            const thumbPositionPercentage = ((offset - thumbHalf) * 100) / this.$el[this.bar.offset];

            // 更新包装器滚动位置
            this.wrap[this.bar.scroll] = (thumbPositionPercentage * this.wrap[this.bar.scrollSize]) / 100;
        },
        /**
         * 鼠标移动事件
         * @param e 事件参数
         */
        mouseMoveDocumentHandler(e) {
            // 只在拖拽过程中响应
            if (!this.cursorDown) {
                return;
            }

            // 取出开始拖拽时的位置
            const prevPage = this[this.bar.axis];

            const offset = (this.$el.getBoundingClientRect()[this.bar.direction] - e[this.bar.client]) * -1;
            const thumbClickPosition = this.$refs.thumb[this.bar.offset] - prevPage;
            const thumbPositionPercentage = ((offset - thumbClickPosition) * 100) / this.$el[this.bar.offset];

            // 更新包装器滚动位置
            this.wrap[this.bar.scroll] = (thumbPositionPercentage * this.wrap[this.bar.scrollSize]) / 100;
        },
        /**
         * 鼠标弹起事件
         * @param e 事件参数
         */
        mouseUpDocumentHandler(e) {
            // 只在拖拽过程中响应
            if (!this.cursorDown) {
                return;
            }

            // 停止拖拽
            this.cursorDown = false;
            this[this.bar.axis] = 0;
        },
        /**
         * 选中事件
         * @param e 事件参数
         */
        mouseSelectStartDocumentHandler(e) {
            // 只在拖拽过程中响应
            if (!this.cursorDown) {
                return;
            }

            // 屏蔽选中操作
            e.preventDefault();
        }
    }
};
</script>

<style lang="scss" scoped>
.sy-scrollbar-bar {
    position: absolute;
    right: 2px;
    bottom: 2px;
    z-index: 9999;
    border-radius: 4px;
    opacity: 0;
    transition: opacity 120ms ease-out;

    .sy-scrollbar-bar-thumb {
        position: relative;
        display: block;
        width: 0;
        height: 0;
        background-color: rgba(144, 147, 153, 0.3);
        border-radius: inherit;
        cursor: pointer;
        transition: 0.3s background-color;

        &:hover {
            background-color: rgba(144, 147, 153, 0.5);
        }
    }
}

.sy-scrollbar-bar.is-vertical {
    top: 2px;
    width: 6px;

    & > div {
        width: 100%;
    }
}

.sy-scrollbar-bar.is-horizontal {
    left: 2px;
    height: 6px;

    & > div {
        height: 100%;
    }
}
</style>
